import { useState } from "react";
import {
  Shield, AlertTriangle, Clock, ShieldCheck,
  FileWarning, Gavel, Bell, FileText, Plus, CalendarClock, AlertCircle,
} from "lucide-react";
import {
  ShieldCheck as PhShieldCheck,
  Warning as PhWarning,
  Clock as PhClock,
  ShieldStar as PhShieldStar,
} from "@phosphor-icons/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatCard } from "@/components/StatCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useHosts } from "@/hooks/useHosts";
import { useBookingStats } from "@/hooks/useBookings";
import { useComplianceActions, useCreateComplianceAction } from "@/hooks/useComplianceActions";
import type { ComplianceActionType } from "@/types/database";

// ─── Renewal policy constants ───────────────────────────────────────────────
const RENEWAL_WARNING_DAYS = 40; // First renewal notification at 40 days before expiry
const RENEWAL_CRITICAL_DAYS = 15; // Critical renewal notification at 15 days before expiry

// ─── Action type display helpers ────────────────────────────────────────────
const ACTION_LABELS: Record<ComplianceActionType, string> = {
  notice: "Notice",
  warning: "Warning",
  penalty: "Penalty",
  order: "Compliance Order",
  reminder: "Renewal Reminder",
};

const ACTION_ICONS: Record<ComplianceActionType, React.ReactNode> = {
  notice: <FileText className="h-3 w-3" />,
  warning: <FileWarning className="h-3 w-3" />,
  penalty: <Gavel className="h-3 w-3" />,
  order: <Gavel className="h-3 w-3" />,
  reminder: <Bell className="h-3 w-3" />,
};

const ACTION_COLORS: Record<ComplianceActionType, string> = {
  notice: "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-warning/10 text-warning border-warning/20",
  penalty: "bg-destructive/10 text-destructive border-destructive/20",
  order: "bg-destructive/10 text-destructive border-destructive/20",
  reminder: "bg-muted text-muted-foreground border-border",
};

export default function Compliance() {
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedHostId, setSelectedHostId] = useState<string>("");
  const [selectedHostName, setSelectedHostName] = useState<string>("");
  const [actionType, setActionType] = useState<ComplianceActionType>("notice");
  const [actionDescription, setActionDescription] = useState("");

  const { data: hosts = [], isLoading } = useHosts();
  const { data: stats } = useBookingStats();
  const { data: complianceActions = [], isLoading: actionsLoading } = useComplianceActions();
  const createAction = useCreateComplianceAction();

  const openActionDialog = (hostId: string, hostName: string) => {
    setSelectedHostId(hostId);
    setSelectedHostName(hostName);
    setActionType("notice");
    setActionDescription("");
    setActionDialogOpen(true);
  };

  const handleSubmitAction = () => {
    if (!selectedHostId || !actionDescription.trim()) return;
    createAction.mutate(
      { hostId: selectedHostId, actionType, description: actionDescription.trim() },
      { onSuccess: () => { setActionDialogOpen(false); setActionDescription(""); } }
    );
  };

  // ─── Computed stats ─────────────────────────────────────────────────────
  const verifiedCount = hosts.filter((h: any) => h.status === "approved" && h.registration_number).length;
  const unverifiedCount = hosts.filter((h: any) => h.registration_number && h.status !== "approved").length;
  const totalValidated = (stats?.approved ?? 0) + (stats?.blocked ?? 0);
  const complianceRate = totalValidated > 0
    ? (((stats?.approved ?? 0) / totalValidated) * 100).toFixed(1)
    : "100.0";

  const now = Date.now();

  // Renewal window is 40 days; critical alert at 15 days before expiry
  const renewalWarningHosts = hosts.filter((h: any) => {
    if (!h.expires_at) return false;
    const daysLeft = (new Date(h.expires_at).getTime() - now) / (1000 * 60 * 60 * 24);
    return daysLeft > 0 && daysLeft <= RENEWAL_WARNING_DAYS;
  });

  const criticalHosts = renewalWarningHosts.filter((h: any) => {
    const daysLeft = (new Date(h.expires_at).getTime() - now) / (1000 * 60 * 60 * 24);
    return daysLeft <= RENEWAL_CRITICAL_DAYS;
  });

  const warningHosts = renewalWarningHosts.filter((h: any) => {
    const daysLeft = (new Date(h.expires_at).getTime() - now) / (1000 * 60 * 60 * 24);
    return daysLeft > RENEWAL_CRITICAL_DAYS;
  });

  const daysUntilExpiry = (expiresAt: string) =>
    Math.ceil((new Date(expiresAt).getTime() - now) / (1000 * 60 * 60 * 24));

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Compliance Monitoring</h1>
          <p className="text-sm text-muted-foreground">
            STR enforcement dashboard — renewal windows, registry status, and compliance actions
          </p>
        </div>

        {/* ── Stat Cards ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard title="Compliance Rate" value={`${complianceRate}%`} icon={PhShieldCheck} iconWeight="duotone" variant="success" />
          <StatCard title="Bookings Blocked" value={stats?.blocked ?? 0} subtitle="All time" icon={PhWarning} iconWeight="duotone" variant="destructive" />
          <StatCard
            title="Renewal Window (40d)"
            value={renewalWarningHosts.length}
            subtitle={criticalHosts.length > 0 ? `${criticalHosts.length} critical (≤15d)` : "None critical"}
            icon={PhClock}
            iconWeight="duotone"
            variant={criticalHosts.length > 0 ? "destructive" : "warning"}
          />
          <StatCard title="Verified Hosts" value={verifiedCount} subtitle="Verification coming soon" icon={PhShieldStar} iconWeight="duotone" variant="info" />
        </div>

        {/* ── Renewal Pipeline ────────────────────────────────────────────── */}
        {renewalWarningHosts.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-warning" />
                    Renewal Pipeline
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Renewal window opens 40 days before expiry. Notifications sent at 40 days and 15 days.
                    If not renewed, registration is cancelled and all bookings are cancelled.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/20">
                  {renewalWarningHosts.length} host{renewalWarningHosts.length !== 1 ? "s" : ""} due
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Critical tier: ≤15 days */}
              {criticalHosts.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-destructive mb-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Critical — ≤15 days (2nd notification threshold)
                  </p>
                  <div className="space-y-2">
                    {criticalHosts.map((h: any) => {
                      const days = daysUntilExpiry(h.expires_at);
                      return (
                        <div key={h.id} className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                          <div>
                            <p className="text-sm font-medium">{h.profile?.full_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">
                              Unit {h.unit?.unit_number || "—"} · Reg# {h.registration_number || "N/A"} · Expires {new Date(h.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">
                              {days}d left
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => openActionDialog(h.id, h.profile?.full_name || "Unknown")}
                            >
                              <Bell className="mr-1 h-3 w-3" /> Log Reminder
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Warning tier: 16–40 days */}
              {warningHosts.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-warning mb-2 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Warning — 16–40 days (1st notification threshold)
                  </p>
                  <div className="space-y-2">
                    {warningHosts.map((h: any) => {
                      const days = daysUntilExpiry(h.expires_at);
                      return (
                        <div key={h.id} className="flex items-center justify-between rounded-md border border-warning/30 bg-warning/5 px-3 py-2">
                          <div>
                            <p className="text-sm font-medium">{h.profile?.full_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">
                              Unit {h.unit?.unit_number || "—"} · Reg# {h.registration_number || "N/A"} · Expires {new Date(h.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/20">
                              {days}d left
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => openActionDialog(h.id, h.profile?.full_name || "Unknown")}
                            >
                              <Bell className="mr-1 h-3 w-3" /> Log Reminder
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Host Compliance Tracker ─────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold">Host Compliance Tracker</CardTitle>
                <CardDescription className="text-xs">Night usage and registry verification per host</CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                {verifiedCount}/{hosts.length} Verified
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : hosts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No hosts registered yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Unit</TableHead>
                    <TableHead className="text-xs">Host</TableHead>
                    <TableHead className="text-xs w-[220px]">Usage</TableHead>
                    <TableHead className="text-xs">Nights</TableHead>
                    <TableHead className="text-xs">Registry</TableHead>
                    <TableHead className="text-xs">Reg. Expiry</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs w-[90px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hosts.map((h: any) => {
                    const used = h.nights_used ?? 0;
                    const limit = h.night_limit ?? 90;
                    const percent = limit > 0 ? Math.round((used / limit) * 100) : 0;
                    const status = percent >= 95 ? "critical" : percent >= 75 ? "warning" : h.status === "expired" ? "expired" : h.status === "pending" ? "pending" : "ok";
                    const isExpiringSoon = h.expires_at && daysUntilExpiry(h.expires_at) <= RENEWAL_WARNING_DAYS && daysUntilExpiry(h.expires_at) > 0;
                    const isCritical = h.expires_at && daysUntilExpiry(h.expires_at) <= RENEWAL_CRITICAL_DAYS && daysUntilExpiry(h.expires_at) > 0;

                    return (
                      <TableRow key={h.id}>
                        <TableCell className="font-medium text-sm">{h.unit?.unit_number || "—"}</TableCell>
                        <TableCell className="text-xs">{h.profile?.full_name || "Unknown"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Progress value={percent} className="h-2 flex-1" />
                            <span className="text-xs font-medium w-10 text-right">{percent}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{used}/{limit}</TableCell>

                        {/* Registry status — verification coming soon */}
                        <TableCell>
                          {h.registration_number ? (
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground font-medium">Coming Soon</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">No Reg #</span>
                          )}
                        </TableCell>

                        <TableCell className="text-xs">
                          {h.expires_at ? (
                            <span className={
                              new Date(h.expires_at) < new Date()
                                ? "text-destructive font-medium"
                                : isCritical
                                ? "text-destructive font-medium"
                                : isExpiringSoon
                                ? "text-warning font-medium"
                                : ""
                            }>
                              {new Date(h.expires_at).toLocaleDateString()}
                              {isCritical && " ⚠"}
                            </span>
                          ) : "—"}
                        </TableCell>

                        <TableCell>
                          {status === "ok" && <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/20">OK</Badge>}
                          {status === "warning" && <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/20">Warning</Badge>}
                          {status === "critical" && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">Critical</Badge>}
                          {status === "expired" && <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">Expired</Badge>}
                          {status === "pending" && <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/20">Pending</Badge>}
                        </TableCell>

                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={() => openActionDialog(h.id, h.profile?.full_name || "Unknown")}
                              >
                                <FileWarning className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Log compliance action</TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Compliance Actions Log ──────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Gavel className="h-4 w-4" />
                  Compliance Actions Log
                </CardTitle>
                <CardDescription className="text-xs">
                  Enforcement actions — notices, warnings, penalties, compliance orders, and renewal reminders.
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={() => { setSelectedHostId(""); setSelectedHostName(""); setActionDescription(""); setActionType("notice"); setActionDialogOpen(true); }}>
                <Plus className="mr-1 h-3 w-3" /> Log Action
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {actionsLoading ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : complianceActions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Shield className="h-8 w-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No compliance actions recorded yet.</p>
                <p className="text-xs text-muted-foreground">Use this log to record notices, warnings, penalties, and compliance orders.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Host</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                    <TableHead className="text-xs">Description</TableHead>
                    <TableHead className="text-xs">Issued By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {complianceActions.map((action: any) => (
                    <TableRow key={action.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(action.issued_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <p className="text-xs font-medium">{action.host?.profile?.full_name || "Unknown"}</p>
                        <p className="text-[10px] text-muted-foreground">Unit {action.host?.unit?.unit_number || "—"}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] flex items-center gap-1 w-fit ${ACTION_COLORS[action.action_type as ComplianceActionType]}`}>
                          {ACTION_ICONS[action.action_type as ComplianceActionType]}
                          {ACTION_LABELS[action.action_type as ComplianceActionType]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate">{action.description}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{action.issued_by}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Log Compliance Action Dialog ────────────────────────────────── */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Log Compliance Action</DialogTitle>
              <DialogDescription>
                Record an enforcement action. All actions are saved to the audit log.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Host selector (shown when dialog opened from "Log Action" button) */}
              {!selectedHostId && (
                <div className="space-y-2">
                  <Label>Host *</Label>
                  <Select value={selectedHostId} onValueChange={(v) => {
                    setSelectedHostId(v);
                    const h = hosts.find((h: any) => h.id === v);
                    setSelectedHostName(h?.profile?.full_name || "Unknown");
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select a host" /></SelectTrigger>
                    <SelectContent>
                      {hosts.map((h: any) => (
                        <SelectItem key={h.id} value={h.id}>
                          {h.profile?.full_name || "Unknown"} — Unit {h.unit?.unit_number || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {selectedHostId && (
                <div className="rounded-md bg-muted px-3 py-2 text-sm">
                  Host: <span className="font-medium">{selectedHostName}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label>Action Type *</Label>
                <Select value={actionType} onValueChange={(v) => setActionType(v as ComplianceActionType)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reminder">📅 Renewal Reminder (40d / 15d threshold)</SelectItem>
                    <SelectItem value="notice">📄 Notice (education / first contact)</SelectItem>
                    <SelectItem value="warning">⚠️ Warning (formal written warning)</SelectItem>
                    <SelectItem value="penalty">💰 Administrative Monetary Penalty</SelectItem>
                    <SelectItem value="order">⚖️ Compliance Order (can be filed in court)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="action_description">Description *</Label>
                <Textarea
                  id="action_description"
                  placeholder="Describe the action taken, reason, applicable rule, and any deadlines..."
                  value={actionDescription}
                  onChange={(e) => setActionDescription(e.target.value)}
                  rows={4}
                />
              </div>
              {actionType === "penalty" && (
                <p className="text-xs text-muted-foreground bg-muted rounded p-2">
                  ⚖️ Penalty amounts depend on your jurisdiction's regulations. Consult local STR legislation for maximum fines.
                </p>
              )}
              {actionType === "order" && (
                <p className="text-xs text-muted-foreground bg-muted rounded p-2">
                  ⚖️ Compliance orders may be enforceable through your local court system if the host fails to comply within the specified timeframe.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSubmitAction}
                disabled={createAction.isPending || !selectedHostId || !actionDescription.trim()}
              >
                {createAction.isPending ? "Logging..." : "Log Action"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
